<template>
  <div class="input-items">
    <a-select
      :placeholder="placeholder"
      :value="newItem"
      :disabled="disabled"
      :options="options"
      :showArrow="false"
      @select="addNewItem"
      @search="searchItems"
      @input-key-down="onPressKey"
      show-search
    />
    <a-flex class="tags_container" v-if="items.length">
      <a-flex class="tags_list">
        <tag-item
          v-for="(item, index) in items"
          :class="`tag${disabled ? '' : ' tag-enabled'}`"
          :key="`item-` + type + '-' + index"
          @click="removeItem(item)"
        >
          #{{ item.name }}
        </tag-item>
      </a-flex>
    </a-flex>
  </div>
</template>

<script setup>
  import { onMounted, ref } from 'vue';
  import { debounce } from 'lodash-es';
  import { useApi } from '@/api/use-api';
  import TagItem from '@/components/tags/TagItem.vue';

  const props = defineProps({
    type: {
      type: String,
      required: true,
    },
    items: {
      type: Array,
      default: function () {
        return [];
      },
    },
    placeholder: {
      type: String,
      default: 'Задайте целевые компетенции',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  });

  const api = useApi();
  const emit = defineEmits(['update']);
  const newItem = ref(null);
  const options = ref([]);
  const searchInput = ref('');
  const isLoading = ref(true);

  onMounted(async () => {
    await searchItems();
    isLoading.value = false;
  });

  const removeItem = (value) => {
    if (!props.disabled) {
      const newList = props.items.filter((el) => {
        return el.name !== value.name;
      });
      emit('update', newList);
    }
  };

  const onPressKey = (event) => {
    if (event.code === 'Enter' || event.code === 'NumpadEnter') {
      event.preventDefault();

      const newItemName = searchInput.value?.trim();
      if (newItemName !== '' && options.value.length === 0) {
        addNewItem(newItemName);
      }
      searchInput.value = '';
    }
  };

  const searchItems = debounce(async (value) => {
    try {
      searchInput.value = value;
      const query = new URLSearchParams();

      if (value) {
        query.set('name', value);
      } else {
        query.set('isMain', '1');
        query.set('per-page', '50');
      }
      query.set('type', props.type);

      const response = await api.competence.index(query);
      options.value = getOptions(response.competencies.items);
    } catch (error) {
      console.log(error);
    }
  }, 300);

  const getOptions = (competencies) => {
    return competencies.map((el) => ({ value: el.name, ...el }));
  };

  const addNewItem = async (value) => {
    newItem.value = null;
    await searchItems();

    if (props.items.find((el) => el.name === value)) return;

    const selectedOption = options.value.find((el) => el.name === value);
    const newList = [
      ...props.items,
      {
        id: selectedOption?.id,
        type: props.type,
        name: value,
        isMain: selectedOption?.isMain ?? false,
      },
    ];
    emit('update', newList);
  };
</script>

<style lang="scss" scoped>
  @use "@/assets/styles/_colors.scss" as *;

  .tags_container {
    background-color: $color-bg-secondary-grey;
    border-radius: 7px;
    max-width: 900px;
    padding: 14px;
    width: 100%;
    margin-top: 14px;

    .tags_list {
      flex-wrap: wrap;
      gap: 4px;

      .tag {
        color: $color-text-secondary-1;
        background: $color-bg-primary-white;
        text-align: center;
        font-size: 12px;
        line-height: 1;
        border-radius: 30px;
        padding: 6px 10px;
        margin: 0 2px 4px 2px;
        white-space: nowrap;
        cursor: unset;

        &:hover {
          color: $color-text-secondary-white;
          background: $color-tag-hover;

          &:after {
            background: url('./assets/cross-white.svg') no-repeat center center
              transparent;
          }
        }
      }

      .tag-enabled {
        cursor: pointer;

        &:after {
          content: '';
          display: inline-block;
          background: url('./assets/cross-black.svg') no-repeat center center
            transparent;
          width: 10px;
          height: 10px;
          margin: 0 0 0 10px;
        }
      }
    }

    .divider {
      border-color: $color-bg-primary-white;
    }
  }
</style>
